import React, {
	Key,
	ReactNode,
	useEffect,
	useRef,
} from 'react';
import { faGripLines } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Divider } from 'antd';
import classNames from 'classnames';
import {
	useDrag,
	useDrop,
} from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import styles from './DraggableListItem.module.css';

export type DragItem<Data> = {
	key: Key;
	data: Data;
	index: number;
	isDragging?: boolean;
};

export type DragObject<Data> = {
	type: 'card';
	dragItem: DragItem<Data>;
};

export type RenderDragItem<Data> = (
	data: Data,
	index: number,
	dragItem: DragItem<Data>,
) => ReactNode;

const DefaultSeparator = () => (<Divider className={styles.slimDivider} />);

type DraggableListItemProps<Data> = {
	dragItem: DragItem<Data>;
	onDragItemChange?: (dragItem: DragItem<Data>) => void;
	renderItem: RenderDragItem<Data>;
	renderSeparator?: RenderDragItem<Data> | null;
	index: number;
	includeSeparator: boolean;
	onMove?: (dragIndex: number, hoverIndex: number) => void;
	onDragStart?: () => void;
	onDrop?: () => void;
	getCanDrag?: (data: Data, index: number, dragItem: DragItem<Data>) => boolean;
};

const DraggableListItem = <Data, >({
	dragItem,
	onDragItemChange,
	renderItem,
	renderSeparator,
	index,
	includeSeparator,
	onMove,
	onDragStart,
	onDrop,
	getCanDrag,
}: DraggableListItemProps<Data>) => {
	const hasDragged = useRef(false);
	const ref = useRef<HTMLDivElement>(null);

	const [_params, drop] = useDrop<DragObject<Data>, void, Record<string, never>>({
		accept: 'card',
		collect: () => ({}),
		hover: (hoverItem, monitor) => {
			if (!ref.current) {
				return;
			}

			const dragIndex = hoverItem.dragItem.index;
			const hoverIndex = index;

			if (dragIndex === hoverIndex) {
				return;
			}

			const hoverBoundingRect = ref.current.getBoundingClientRect();
			const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
			const clientOffset = monitor.getClientOffset();

			if (clientOffset == null) {
				return;
			}

			const hoverClientY = clientOffset.y - hoverBoundingRect.top;

			if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
				return;
			}

			if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
				return;
			}

			if (onMove != null) {
				onMove(dragIndex, hoverIndex);
			}

			hoverItem.dragItem.index = hoverIndex;
		},
	});

	const [{ isDragging }, drag, preview] = useDrag<DragObject<Data>, void, { isDragging: boolean }>({
		item: {
			type: 'card',
			dragItem,
		},
		collect: (monitor) => ({
			isDragging: monitor.isDragging(),
		}),
		begin() {
			onDragItemChange?.({ ...dragItem, isDragging: true });

			if (onDragStart != null) {
				onDragStart();
			}
		},
		end() {
			onDragItemChange?.({ ...dragItem, isDragging: false });
		},
	});

	useEffect(() => {
		if (isDragging) {
			hasDragged.current = true;

			return;
		}

		if (hasDragged.current) {
			if (onDrop != null) {
				onDrop();
			}

			hasDragged.current = false;
		}
	}, [isDragging, onDrop, dragItem]);

	useEffect(() => {
		preview(getEmptyImage());
	}, [preview]);

	const canDrag = getCanDrag == null || getCanDrag(dragItem.data, index, dragItem);

	if (canDrag) {
		drag(drop(ref));
	}

	return (
		<div ref={ref}>
			<div
				className={classNames(styles.item, {
					[styles.dragging]: isDragging,
				})}
			>
				<FontAwesomeIcon
					className={classNames(styles.grip, {
						[styles.dragDisabled]: !canDrag,
					})}
					icon={faGripLines}
				/>
				<div className={styles.content}>
					{renderItem(dragItem.data, index, dragItem)}
				</div>
			</div>
			{includeSeparator && renderSeparator != null && (
				renderSeparator(dragItem.data, index, dragItem)
			)}
			{includeSeparator && renderSeparator == null && (
				<DefaultSeparator />
			)}
		</div>
	);
};

export default DraggableListItem;
